/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

const name = 'Name';
const email = 'Email';
const role = 'Role';
const admin = 'Admin';
const member = 'Member';

export const organizationsPageEn = {
  organizations: 'Organizations',
  description: 'Manage your memberships in organizations',
  memberships: 'Memberships',
  members: 'Members',
  name: name,
  role: role,
  admin: admin,
  member: member,
  numberOfMembers: 'Number of Members',
  create: 'Create Organization',
  edit: 'Edit Organization',
  invitation: {
    title: 'Do you want to join "{{organizationName}}"?',
    contentTop:
      'Someone has invited you to the organization "{{organizationName}}".',
    contentBottom: 'Do you want to join the organization?',
    accept: 'Join Organization',
    reject: 'Decline'
  },
  empty: {
    top: "You haven't joined any organization yet.",
    bottom:
      'Here, organizations such as companies or associations can create a central profile for all solutions.'
  },
  info: {
    title: 'About Organizations',
    content: {
      title: 'What are organizations for?',
      message:
        "Through your account, you can centrally create and manage organizations – for example, for your association or your company.<br/><br/>Thanks to central management, connected solutions can adopt information such as the description text or logo of the organization or automatically grant rights to members (for example, access to a solution). This eliminates the tedious task of creating the same organization and its members again in each solution.<br/><br/>  Within an organization, there are two roles. Admins have permission to manage the organization and invite additional people. Regular members can access the organization's profile page but cannot make changes."
    }
  },
  createEdit: {
    new: 'Create New Organization',
    edit: 'Edit Organization',
    create: 'Create Organization',
    logoPicture: 'Organization Logo',
    name: 'Organization Name',
    description: 'Public Description',
    logo: {
      deletionConfirmationTitle: 'Remove logo?',
      deletionConfirmationQuestion: 'Should your organization logo be deleted?',
      acceptDeletionConfirmation: 'Yes, remove!',
      declineDeletionConfirmation: 'No',
      discardChanges: 'Discard',
      acceptChanges: 'Apply',
      tooLargeFile: {
        title: 'Image too large (max. 5 MB)',
        content:
          'Unfortunately, the selected image is too large. Please select an image with a maximum of 5 MB.'
      },
      invalidFileType: {
        title: 'File format not supported',
        content:
          'Unfortunately, we do not support the format of the selected file. Please select an image in "jpg", "jpeg" or "png" format.'
      },
      uploadLogoAlt: 'Round image of a camera',
      defaultAlt: 'Round organization logo',
      uploadButtonLabel: 'Button to select a new organization logo to upload',
      deleteButtonLabel: 'Delete button',
      rotateButtonLabel: 'Rotate button'
    },
    shortDescription: 'Short Description',
    detailedDescription: 'Detailed Description',
    websiteUrl: 'Website',
    phoneNumber: 'Phone',
    address: 'Address',
    vatNumber: 'VAT Number',
    email: email,
    required: '{{field}} is a required field.',
    counter: '{{count}} / {{maxCount}} characters',
    maxLength: 'Maximum character length of {{count}} exceeded',
    invalidUrl: 'Invalid URL',
    invalidEmail: 'Invalid email address',
    save: 'Apply',
    mandatoryField: '*Required field'
  },
  details: {
    options: {
      menuLabel: 'Organization Settings',
      edit: 'Edit Organization',
      delete: 'Delete Organization',
      leave: 'Leave Organization',
      manageKey: 'Manage Key'
    },
    keyManagement: {
      title: 'Manage Key',
      keyFileNamePrefix: '{{appName}}-Key',
      keyFileName: '{{appName}}-Key_{{organizationName}}_{{timestamp}}.asc',
      createdOn: 'Created on {{date}} at {{time}}',
      expiresOn: 'Valid until {{date}} at {{time}}',
      create: {
        title: 'Create Key',
        description:
          'Certain solutions, e.g., from the medical field, require special protection of sensitive data. A digital key is used for encryption to restrict access to authorized persons.<br/><br/>Create a key for your organization here. The key applies to all members and can be used in corresponding solutions.'
      },
      manage: {
        title: 'Manage Key',
        description:
          "Certain solutions, e.g., from the medical field, require special protection of sensitive data. A digital key is used for encryption to restrict access to authorized persons.<br/><br/>Manage your organization's key here. The key applies to all members and can be used in corresponding solutions."
      },
      delete: {
        title: 'Delete Key?',
        content:
          'Without a key, access to solutions and data that require a key is no longer possible. Do you want to continue?',
        cancel: 'Cancel',
        confirm: 'Delete Key'
      },
      refresh: {
        title: 'Renew Key?',
        content:
          'The old key will be irreversibly replaced by a new one. Do you want to continue?',
        cancel: 'Cancel',
        confirm: 'Renew Key'
      },
      button: {
        create: 'Create Key',
        download: 'Download',
        refresh: 'Renew',
        delete: 'Delete'
      },
      toast: {
        keyExpired: {
          title: 'Key no longer valid!',
          content:
            'Please renew the key to continue accessing solutions that require a valid key.'
        },
        newKeyGenerated: {
          title: 'Important: Download new key now!',
          content:
            'Download the key before closing the window. After that, access is no longer possible. Only distribute the key to others via secure channels (e.g., as an encrypted email).'
        },
        oldKeyRefreshed: {
          title: 'Keep the old key safe!',
          content:
            'It is possible that you will need the old key again to access older data. Therefore, continue to keep the key safe.'
        },
        email: {
          title: 'Organization email address required',
          content:
            "To create a key, an email address for the organization is required. Please edit your organization's profile and add an email address.",
          button: 'Edit Organization Profile'
        }
      }
    },
    noAccess:
      'You do not seem to have the required permissions to access this organization or this organization does not exist.',
    navigateToOverview: 'Back to Overview',
    organizationLogoAlt: 'Logo of organization {{organization}}',
    description: 'Description',
    learnMore: 'Learn more...',
    aboutUs: 'About Us',
    noMemberName: 'Name not provided',
    inviteUser: 'Invite Person',
    memberCount_one: '{{count}} Member',
    memberCount_other: '{{count}} Members',
    invitationCount_one: '{{count}} Invitation',
    invitationCount_other: '{{count}} Invitations',
    email: email,
    name: name,
    role: role,
    admin: admin,
    member: member,
    listOptions: 'Options',
    listActions: {
      menuLabel: 'Options',
      admin: admin,
      member: member,
      changeRole: 'Change Role',
      delete: 'Remove',
      resendInvitation: 'Resend Invitation'
    },
    invite: {
      title: 'Invite Person',
      email: 'Email',
      send: 'Send Invitation',
      addMore: 'Add More',
      emailPlaceholder: 'Email Address',
      requiredEmail: 'Email address cannot be empty.',
      invalidEmail: 'Invalid email address',
      existingEmail: 'This email address has already been entered.',
      role: role,
      admin: admin,
      member: member,
      roleTooltip: {
        admin:
          '<strong>Admin:</strong> Can change all settings and add members. <br>',
        member: '<strong>Member:</strong> Can access this organization.'
      },
      mandatoryField: '*Required field'
    },
    delete: {
      title: 'Delete Organization?',
      content:
        'By deleting the organization, all content and members of the group will be irreversibly removed.',
      cancel: 'Cancel',
      confirm: 'Delete Organization'
    },
    deleteBlocked: {
      title: 'Organization cannot be deleted',
      content:
        'Before you can delete the organization, you must first remove all offers and close or cancel all booking requests and bookings. Currently remaining: Offers: {{offerCount}}, Booking requests: {{bookingRequestCount}}, Bookings: {{bookingCount}}.',
      cancel: 'Close',
      confirm: 'Got it'
    },
    leave: {
      title: 'Leave Organization?',
      content:
        'If you leave the organization, you will no longer have any access to the organization.',
      cancel: 'Cancel',
      confirm: 'Leave Organization'
    },
    removeMember: {
      title: 'Remove Member?',
      content:
        '{{name}} will be removed from the organization and will no longer have any access to the organization.',
      cancel: 'Cancel',
      confirm: 'Remove Member'
    },
    updateRole: {
      title: 'Change Role?',
      content:
        '{{name}}\'s role in this organization will be changed to "{{role}}".',
      cancel: 'Cancel',
      confirm: 'Change Role'
    },
    removeInvitation: {
      title: 'Revoke Invitation?',
      content: 'The invitation for {{name}} will be revoked.',
      cancel: 'Cancel',
      confirm: 'Revoke Invitation'
    },
    updateInvitationRole: {
      title: 'Change Role?',
      content:
        '{{name}}\'s role in this organization will be changed to "{{role}}".',
      cancel: 'Cancel',
      confirm: 'Change Role'
    }
  }
};
